"use client";

import { useState, useRef, useEffect } from "react";
import { X, ImagePlus, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useProgramCardStore, ProgramCardTab, ProgramCard, ProgramCardColor } from "@/store/programCardStore";
import { ActivityLogger } from "@/lib/activityLog";
import { prepareImageUpload } from "@/lib/image-upload";
import { TranslationPanel } from "@/components/admin/TranslationPanel";
import { getStrapiImageUrl } from "@/lib/image-utils";

interface CreateProgramCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
  editCard?: ProgramCard | null;
}

const TAB_OPTIONS: { value: ProgramCardTab; labelKey: keyof ReturnType<typeof useTabLabels> }[] = [
  { value: 'collective-impact', labelKey: 'collectiveImpact' },
  { value: 'our-programs', labelKey: 'ourPrograms' },
  { value: 'funding-grants', labelKey: 'fundingGrants' },
  { value: 'facilitating-solutions', labelKey: 'facilitatingSolutions' },
];

const COLOR_SWATCHES: { value: ProgramCardColor; hex: string }[] = [
  { value: 'light', hex: '#EFF2F1' },
  { value: 'cream', hex: '#F8F5F0' },
  { value: 'sage', hex: '#CED9D8' },
];

const VALID_COLORS = new Set<ProgramCardColor>(['light', 'cream', 'sage']);

function useTabLabels() {
  const { t } = useLanguage();
  return t.programCard.tabs;
}

export default function CreateProgramCardModal({ isOpen, onClose, onResult, editCard }: CreateProgramCardModalProps) {
  const { currentUser } = useAuth();
  const { t, language } = useLanguage();
  const { createProgramCard, updateProgramCard } = useProgramCardStore();
  const pc = t.programCard;
  const isEditMode = !!editCard;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tab, setTab] = useState<ProgramCardTab | ''>('');
  const [cardColor, setCardColor] = useState<ProgramCardColor>('sage');
  /** undefined = keep existing | null = explicitly cleared | File = new upload */
  const [photo, setPhoto] = useState<File | null | undefined>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [showTabDropdown, setShowTabDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  /**
   * MWBI-900 dirty-tracking. Holds ONLY the Spanish fields the admin actually edited (and
   * translationLocked when they reset to automatic). Sending back untouched Spanish would
   * diverge the fingerprint the CMS stores and silently freeze this item's translation.
   */
  const [translationEdits, setTranslationEdits] = useState<Record<string, string | boolean>>({});
  const [spanishTitle, setSpanishTitle] = useState('');
  const [spanishDescription, setSpanishDescription] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const tabLabels = useTabLabels();

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTab('');
    setCardColor('sage');
    setPhoto(null);       // create mode: no photo
    setPhotoPreview(null);
    setPhotoError(null);
    setTranslationEdits({});
    setSpanishTitle('');
    setSpanishDescription('');
  };

  // Pre-fill form when editing an existing card
  useEffect(() => {
    if (editCard) {
      setTitle(editCard.title);
      setDescription(editCard.description);
      setTab(editCard.tab);
      setCardColor(editCard.cardColor && VALID_COLORS.has(editCard.cardColor) ? editCard.cardColor : 'sage');
      setPhoto(undefined); // keep existing photo unless user explicitly changes/removes it
      setPhotoPreview(editCard.photo?.url ? getStrapiImageUrl(editCard.photo.url) ?? null : null);
      setPhotoError(null);
      setTranslationEdits({});
      setSpanishTitle(editCard.titleEs ?? '');
      setSpanishDescription(editCard.descriptionEs ?? '');
    } else {
      resetForm();
    }
  }, [editCard, isOpen]);

  /**
   * The parent renders this modal as <CreateProgramCardModal isOpen={...} />, so it stays
   * MOUNTED while hidden and these hooks keep running. Clearing the edits it produced on
   * deactivation -- not only re-seeding them on activation -- stops item A's pending
   * Spanish edits from being carried into item B when the modal is reopened.
   */
  useEffect(() => {
    if (!isOpen) setTranslationEdits({});
  }, [isOpen]);

  if (!isOpen) return null;

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { file: prepared, error } = await prepareImageUpload(file, language);
    if (error) {
      setPhotoError(error);
      return;
    }
    setPhotoError(null);
    setPhoto(prepared);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(prepared);
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSpanishChange = (name: string, value: string) => {
    if (name === 'titleEs') setSpanishTitle(value);
    if (name === 'descriptionEs') setSpanishDescription(value);
    setTranslationEdits((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * "Reset to automatic" sends BOTH re-arm signals the CMS understands: clear the lock and
   * blank the Spanish, so the next save retranslates from the English.
   */
  const handleResetTranslation = () => {
    setSpanishTitle('');
    setSpanishDescription('');
    setTranslationEdits({ titleEs: '', descriptionEs: '', translationLocked: false });
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim() || !tab || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const cardData = {
        title: title.trim(),
        description: description.trim(),
        tab: tab as ProgramCardTab,
        cardColor,
        photo,
      };
      const result = isEditMode
        ? await updateProgramCard(editCard!.documentId, {
            ...cardData,
            // Only fields the admin actually touched.
            ...(Object.keys(translationEdits).length > 0
              ? { translationFields: translationEdits }
              : {}),
          })
        : await createProgramCard(cardData);

      if (result) {
        onResult?.(true, isEditMode ? pc.updated ?? 'Card updated successfully' : pc.created);
        if (currentUser) {
          if (editCard) ActivityLogger.editProgramCard(currentUser, cardData.title, editCard.documentId);
          else ActivityLogger.createProgramCard(currentUser, cardData.title, result.documentId);
        }
        resetForm();
        onClose();
      } else {
        onResult?.(false, pc.failed);
      }
    } catch (err) {
      console.error('CreateProgramCardModal', err);
      onResult?.(false, pc.failed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = title.trim().length > 0 && description.trim().length > 0 && tab !== '';
  const selectedTabLabel = tab ? tabLabels[TAB_OPTIONS.find((o) => o.value === tab)!.labelKey] : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background z-10">
          <h2 className="text-lg font-semibold text-foreground">
            {isEditMode ? pc.editModalTitle : pc.modalTitle}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-sidebar-hover rounded transition">
            <X size={20} className="text-foreground-muted" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Current user */}
          {currentUser && (
            <div className="flex items-center gap-2">
              {currentUser.avatarUrl ? (
                <img
                  src={getStrapiImageUrl(currentUser.avatarUrl) ?? currentUser.avatarUrl}
                  alt={currentUser.displayName}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
                  style={{ backgroundColor: currentUser.avatarColor }}
                >
                  {currentUser.initials}
                </div>
              )}
              <span className="text-sm font-medium text-foreground">{currentUser.displayName}</span>
            </div>
          )}

          {/* Tab selector */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{pc.tabLabel}</label>
            <div className="relative">
              <button
                type="button"
                data-testid="create-program-card-tab-dropdown-btn"
                onClick={() => setShowTabDropdown((s) => !s)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-left text-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <span className={selectedTabLabel ? 'text-foreground' : 'text-foreground-muted'}>
                  {selectedTabLabel || pc.tabPlaceholder}
                </span>
                <ChevronDown size={16} className={`text-foreground-muted transition-transform ${showTabDropdown ? 'rotate-180' : ''}`} />
              </button>
              {showTabDropdown && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
                  {TAB_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`create-program-card-tab-option-${opt.value}`}
                      onClick={() => { setTab(opt.value); setShowTabDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-sidebar-hover transition ${tab === opt.value ? 'text-primary font-medium' : 'text-foreground'}`}
                    >
                      {tabLabels[opt.labelKey]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {pc.titleLabel} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              data-testid="create-program-card-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={pc.titlePlaceholder}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              {pc.descriptionLabel} <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="create-program-card-description-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={pc.descriptionPlaceholder}
              rows={5}
              className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Spanish (auto-translated) — edit mode only; a new card has nothing to show yet */}
          {isEditMode && editCard && (
            <TranslationPanel
              isActive={isOpen}
              disabled={isSubmitting}
              status={editCard.translationStatus}
              locked={
                typeof translationEdits.translationLocked === 'boolean'
                  ? translationEdits.translationLocked
                  : editCard.translationLocked
              }
              testIdPrefix="program-card-translation"
              fields={[
                { name: 'titleEs', label: pc.titleLabel, value: spanishTitle },
                {
                  name: 'descriptionEs',
                  label: pc.descriptionLabel,
                  value: spanishDescription,
                  multiline: true,
                },
              ]}
              onChange={handleSpanishChange}
              onResetToAutomatic={handleResetTranslation}
            />
          )}

          {/* Card color picker */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{pc.colorLabel}</label>
            <div className="flex gap-3" role="radiogroup" aria-label={pc.colorLabel}>
              {COLOR_SWATCHES.map(({ value, hex }) => {
                const selected = cardColor === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    aria-label={pc.colors[value]}
                    title={pc.colors[value]}
                    onClick={() => setCardColor(value)}
                    data-testid={`program-card-color-${value}`}
                    className={`w-10 h-10 rounded-full border-2 transition focus:outline-none focus:ring-2 focus:ring-primary ${
                      selected ? 'border-primary ring-2 ring-primary ring-offset-2' : 'border-border hover:border-foreground-muted'
                    }`}
                    style={{ backgroundColor: hex }}
                  />
                );
              })}
            </div>
          </div>

          {/* Photo upload */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">{pc.uploadPhoto}</label>
            {photoPreview ? (
              <div className="relative aspect-video rounded-md overflow-hidden border border-border">
                <img src={photoPreview} alt="Preview" className="w-full h-full object-contain" />
                <button
                  onClick={removePhoto}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-primary/50 transition"
              >
                <ImagePlus className="mx-auto mb-2 text-foreground-muted" size={32} />
                <button
                  type="button"
                  className="mt-1 px-4 py-1.5 border border-border rounded-md text-sm font-medium text-foreground hover:bg-sidebar-hover transition"
                >
                  {pc.chooseFile}
                </button>
                <p className="text-xs text-foreground-muted mt-2">{pc.photoHint}</p>
              </div>
            )}
            {photoError && <p className="text-xs text-red-500 mt-1">{photoError}</p>}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            onClick={() => { resetForm(); onClose(); }}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition disabled:opacity-50"
          >
            {pc.cancel}
          </button>
          <button
            data-testid="create-program-card-submit-btn"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? (isEditMode ? pc.updating : pc.creating)
              : (isEditMode ? pc.editModalTitle : pc.modalTitle)}
          </button>
        </div>
      </div>
    </div>
  );
}
